import * as React from 'react';
import { styled } from '@linaria/react';

import useTickSfx from '@/hooks/use-tick-sfx';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Space from '@/components/Space';

import {
  Svg,
  Label,
  PositionDot,
  CodeSnippet,
  Coord,
  Row,
} from './BasicShapes.shared';

const SIZE = 300;

const POINTS = [
  [60, 100],
  [100, 180],
  [140, 140],
  [180, 180],
  [220, 100],
];

function BasicPolygonDemo() {
  const playTick = useTickSfx();

  return (
    <Demo
      id="basic-polygon-demo"
      outputWrapper={OutputWrapper}
      initialValues={{
        cx: POINTS[POINTS.length - 1][0],
        cy: POINTS[POINTS.length - 1][1],
      }}
      CustomGridComponent={ControlsGrid}
      controls={[
        <DemoSliderControl
          highlightControlWhenActive
          key="cx"
          id="cx"
          label="x"
          secondaryLabel="(final point)"
          min={0}
          max={SIZE}
          step={10}
          wrapperStyle={{
            gridArea: 'cx',
          }}
          onChange={() => {
            playTick();
          }}
        />,
        <DemoSliderControl
          highlightControlWhenActive
          key="cy"
          id="cy"
          label="y"
          secondaryLabel="(final point)"
          min={0}
          max={SIZE}
          step={10}
          wrapperStyle={{
            gridArea: 'cy',
          }}
          onChange={() => {
            playTick();
          }}
        />,
      ]}
    >
      {({ cx, cy }, activeControlId) => {
        const actualPoints = POINTS.map(([x, y], index) => {
          if (index === POINTS.length - 1) {
            return [cx, cy];
          }
          return [x, y];
        });

        // Format as strings for the shown code and the actual <polyline> element
        const pointsArr = actualPoints.map(([x, y]) => {
          return `${x},${y}`;
        });
        const pointsInline = pointsArr.join(' ');
        const pointsIndented =
          '\n      ' + pointsArr.join('\n      ') + '\n    ';

        const code = `\
<svg width="${SIZE}" height="${SIZE}">
  <polygon
    points="${pointsIndented}"
  />
</svg>\
`;

        return (
          <StyledRow>
            <StyledCodeSnippet code={code} />

            <SvgWrapper>
              <Svg size={SIZE}>
                {/* MAIN SHAPE: */}
                <polygon
                  points={pointsInline}
                  fill="none"
                  stroke="var(--wham-complementary-500)"
                  strokeWidth={5}
                />

                {actualPoints.map(([x, y], index) => {
                  const labelAbove = index % 2 === 0;
                  const labelOffset = 12;
                  const isFinalPoint = index === POINTS.length - 1;

                  return (
                    <React.Fragment key={index}>
                      <Label
                        x={x}
                        y={
                          labelAbove
                            ? y - labelOffset
                            : y + labelOffset
                        }
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{
                          fontSize: '0.625rem',
                        }}
                      >
                        (
                        <Coord
                          data-is-active={
                            isFinalPoint && activeControlId === 'cx'
                          }
                        >
                          {x}
                        </Coord>
                        ,{' '}
                        <Coord
                          data-is-active={
                            isFinalPoint && activeControlId === 'cy'
                          }
                        >
                          {y}
                        </Coord>
                        )
                      </Label>
                      <PositionDot key={`${x}-${y}`} cx={x} cy={y} />
                    </React.Fragment>
                  );
                })}

                <PositionDot cx={cx} cy={cy} />
              </Svg>
            </SvgWrapper>
          </StyledRow>
        );
      }}
    </Demo>
  );
}

const OutputWrapper = styled(Space)`
  container-type: inline-size;
  background: var(--color-background);
`;

const ControlsGrid = styled.div`
  display: grid;
  gap: 16px 32px;
  grid-template-areas:
    'cx'
    'cy';

  @media (min-width: 30rem) {
    grid-template-areas: 'cx cy';
  }
`;

const StyledRow = styled(Row)`
  @container (max-width: 37rem) {
    flex-direction: column;
    align-items: center;
  }
`;

const StyledCodeSnippet = styled(CodeSnippet)`
  max-height: ${SIZE}px;
  overflow: auto;

  @container (max-width: 37rem) {
    width: 100%;
  }
`;

const SvgWrapper = styled.div`
  display: flex;

  @container (max-width: 37rem) {
    position: relative;
  }
`;

export default BasicPolygonDemo;
